import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";

import {
    getUnitWiseCost,
    getMonitoringProjectList,
} from "../../redux/adminUnitReportSlice/adminUnitReportSlice";

import { getAllUnits } from "../../redux/unitSlice/unitSlice";

import CustomTable from "../../components/Common/CustomTable/CustomTable";
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";

import PMGProjectIcon from "../../assets/icons/pmg-project.svg";

import {
    Loader2,
    BarChart3,
    Sigma
} from "lucide-react";

const PROJECT_TYPE_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "EPC", value: "EPC" },
    { label: "BTG", value: "BTG" },
    { label: "OTHER", value: "OTHER" },
];

const COST_LEVEL_OPTIONS = [
    { label: "Direct Material", value: "DIRECT_MATERIAL" },
    { label: "Conversion Cost", value: "CONVERSION_COST" },
    { label: "Factory Cost Level", value: "TOTAL_COST" },
];

const VALUE_MODE_OPTIONS = [
    { label: "Crore", value: "CRORE" },
    { label: "Percent", value: "PERCENT" },
];

const TIME_FILTER_OPTIONS = [
    { label: "Year Wise", value: "YEAR" },
    { label: "Quarter Wise", value: "QUARTER" },
];


/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

const centerHeader = {
    onHeaderCell: () => ({ style: { textAlign: "center" } }),
};

const toNumber = (val) => {
    const cleaned = String(val ?? "")
        .replace(/,/g, "")
        .trim();

    const n = parseFloat(cleaned);

    return Number.isFinite(n)
        ? n
        : 0;
};

// Upto = record.financial_years.upto + every FY strictly before the
// current FY. Mirrors DMProjectWiseReport's getUptoMarchValue, anchored
// to CURRENT_FY_START (never to a selected filter, since this report has
// no "selected year" concept).
const getUptoValue = (record) => {
    if (!record) return "0.00";

    let sum = toNumber(record.financial_years?.upto);

    Object.entries(record.financial_years || {}).forEach(([fyKey, val]) => {
        if (fyKey === "upto") return;
        if (getFYStartYear(fyKey) < CURRENT_FY_START) {
            sum += toNumber(val);
        }
    });

    return sum.toFixed(2);
};

const formatCellValue = (val, valueMode) => {

    if (
        val === undefined ||
        val === null ||
        val === ""
    ) {
        return val;
    }

    return valueMode === "PERCENT"
        ? `${val}%`
        : val;
};


// Creates a unique key for project selection.
const makeProjectKey = (ioNo, revNo) => {

    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();

    return `${io}-${rev}`;
};


/* -------------------------------------------------------------------------- */
/*                         CURRENT FINANCIAL YEAR HELPERS                     */
/* -------------------------------------------------------------------------- */

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {

    const today = new Date();

    return today.getMonth() >= 3
        ? today.getFullYear()
        : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
    if (!startDate) return null;

    const [day, month, year] = startDate.split("-").map(Number);

    if (!day || !month || !year) return null;

    return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
// NEW: recomputed via applySystemFYStart() once per render (see component
// body) so every helper below - all of which close over these module
// bindings - always sees the latest System Start Date.
let CURRENT_FY_START = getCurrentFYStart();

let CURRENT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`;

const applySystemFYStart = (startDate) => {
    const resolved = parseSystemFYStart(startDate) ?? getCurrentFYStart();

    if (resolved === CURRENT_FY_START) return;

    CURRENT_FY_START = resolved;
    CURRENT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`;
};


/* -------------------------------------------------------------------------- */
/*                               FINANCIAL YEAR                               */
/* -------------------------------------------------------------------------- */

const getFYStartYear = (fyKey) => {
    return Number(
        String(fyKey).split("-")[0]
    );
};


// Converts:
// "Q1-2026" -> "2026-2027"
const getFYFromQuarterKey = (quarterKey) => {

    const match = /^Q[1-4]-(\d{4})$/.exec(
        quarterKey
    );

    if (!match) return null;

    const fyStart = Number(match[1]);

    return `${fyStart}-${fyStart + 1}`;
};


// Converts:
// "Q1-2026" -> "Q1"
const getQuarterLabel = (quarterKey) => {
    return quarterKey.split("-")[0];
};


/* -------------------------------------------------------------------------- */
/*                              DYNAMIC UPTO LOGIC                            */
/* -------------------------------------------------------------------------- */

// YEAR VIEW
// Upto 2026

const getYearWiseUptoLabel = () => {
    return `Upto Mar ${CURRENT_FY_START}`;
};


// QUARTER VIEW
// Upto Mar 26

const getQuarterWiseUptoLabel = () => {

    const shortYear =
        String(CURRENT_FY_START).slice(-2);

    return `Upto Mar ${shortYear}`;
};


/* -------------------------------------------------------------------------- */
/*                          FINANCIAL YEAR FILTERING                          */
/* -------------------------------------------------------------------------- */


// Only show current FY onwards.

const getVisibleFinancialYears = (labels = []) => {

    return labels.filter((fy) => {

        return (
            getFYStartYear(fy)
            >= CURRENT_FY_START
        );

    });

};


// Groups quarters by FY.
//
// Example:
//
// {
//      "2026-2027": [
//          "Q1-2026",
//          "Q2-2026",
//          "Q3-2026",
//          "Q4-2026"
//      ],
//
//      "2027-2028": [
//          ...
//      ]
// }

const groupQuarterLabelsByFY = (
    quarterLabels = []
) => {

    const grouped = {};

    quarterLabels.forEach((quarter) => {

        const fy = getFYFromQuarterKey(
            quarter
        );

        if (!fy) return;

        if (
            getFYStartYear(fy)
            < CURRENT_FY_START
        ) {
            return;
        }

        if (!grouped[fy]) {
            grouped[fy] = [];
        }

        grouped[fy].push(quarter);

    });


    Object.keys(grouped).forEach((fy) => {

        grouped[fy].sort((a, b) =>
            a.localeCompare(b)
        );

    });


    return grouped;
};

const DMUnitWiseReport = () => {

    const dispatch = useDispatch();


    /*                                  STATES                                */


    const [projectType, setProjectType] = useState("ALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("PERCENT");

    const [timeFilter, setTimeFilter] = useState("YEAR");
    const [showUptoMarch, setShowUptoMarch] = useState(false);

    // [] => All Projects
    // ["IO_NO-REV_NO"] => Selected Projects
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    // Stores all projects fetched from project wise API.
    // Used only for populating the Projects Filter.
    const [masterProjects, setMasterProjects] = useState([]);
    const monitoringProjectTypeRef = useRef(projectType);

    const {
        unitWiseCost,
        gettingUnitWiseCost,
        monitoringProjectList,
    } = useSelector((state) => state.unitReport);


    const { allUnitsList } = useSelector((state) => state.unit);
    const { systemDate } = useSelector((state) => state.otherProjects);

    // Recomputes CURRENT_FY_START/CURRENT_FY_KEY (module-level, closed over
    // by the helpers above) synchronously during render, before anything
    // below reads them.
    applySystemFYStart(systemDate?.STARTDATE);

    const reportData =
        unitWiseCost?.data ??
        unitWiseCost ??
        null;

    const financialYearLabels =
        reportData?.labels ?? [];

    const quarterlyLabels =
        reportData?.quarterly_labels ?? [];

    const apiUnits =
        reportData?.units ?? [];

    const overall =
        reportData?.overall ?? null;


    const fetchAllUnits = () => {
        dispatch(getAllUnits());
    };


    const fetchMonitoringProjects = () => {
        monitoringProjectTypeRef.current = projectType;
        dispatch(
            getMonitoringProjectList({
                PROJECT_TYPE: projectType,
            })
        );

    };


    const fetchReport = () => {

        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProject.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({
                IO_NO,
                REV_NO,
            }));


        dispatch(
            getUnitWiseCost({
                PROJECT_TYPE: projectType,
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                projects: projectsPayload,
            })
        );

    };

    // Fetch all units once.

    useEffect(() => {
        fetchAllUnits();
    }, []);

    useEffect(() => {
        console.log("monitoringProjectList:", monitoringProjectList);
    }, [monitoringProjectList]);

    // Project type affects:
    // - Monitoring project list.
    // - Unit wise cost report.
    // - Projects filter options.

    useEffect(() => {

        setSelectedProjectKeys([]);

        fetchMonitoringProjects();
        fetchReport();

        // eslint-disable-next-line
    }, [projectType]);


    // Backend dependent filters.
    // Skip the very first run — the [projectType] effect above already
    // fires the initial fetch, so this effect would otherwise double it.
    const isUnitWiseCostMount = useRef(true);

    useEffect(() => {

        if (isUnitWiseCostMount.current) {
            isUnitWiseCostMount.current = false;
            return;
        }

        fetchReport();

        // eslint-disable-next-line

    }, [
        costLevel,
        valueMode,
        selectedProjectKeys.join(","),
    ]);



    /*PROJECT FILTER LOGIC*/

    // Populate master projects from Monitoring API.

    useEffect(() => {

        let projects = monitoringProjectList;

        if (projects && !Array.isArray(projects)) {

            projects =
                projects.data ??
                projects.projects ??
                projects.data?.projects ??
                [];

        }

        if (!Array.isArray(projects)) {
            projects = [];
        }


        setMasterProjects((prev) => {

            const map = new Map(
                prev.map((project) => [

                    makeProjectKey(
                        project.IO_NO,
                        project.REV_NO
                    ),

                    project,
                ])
            );


            projects.forEach((project) => {

                map.set(

                    makeProjectKey(
                        project.IO_NO,
                        project.REV_NO
                    ),

                    {
                        IO_NO: project.IO_NO,
                        REV_NO: project.REV_NO,
                        PROJ_NAME: project.PROJ_NAME,
                        // PROJECT_TYPE: projectType,
                       PROJECT_TYPE: project.PROJECT_TYPE ?? monitoringProjectTypeRef.current,
                    }

                );

            });


            return Array.from(map.values());

        });

    }, [
        monitoringProjectList,
        // projectType,
    ]);


    // Projects shown in the filter dropdown.

    const visibleProjectsForFilter = useMemo(() => {

        if (projectType === "ALL") {
            return masterProjects;
        }

        return masterProjects.filter(
            (project) =>
                project.PROJECT_TYPE === projectType
        );

    }, [
        masterProjects,
        projectType,
    ]);


    // Used while creating the API payload.

    const projectKeyToProject = useMemo(() => {

        const map = new Map();

        masterProjects.forEach((project) => {

            map.set(

                makeProjectKey(
                    project.IO_NO,
                    project.REV_NO
                ),

                project

            );

        });

        return map;

    }, [masterProjects]);



    /*                           UNIT MAPPING LOGIC                           */



    // Master Unit List from getAllUnits API.

    const masterUnits = allUnitsList ?? [];

    // Quick lookup map.
    //
    // key:
    // RP
    //
    // value:
    // entire unit object from unit wise cost API.

    const unitCostMap = useMemo(() => {

        const map = new Map();

        apiUnits.forEach((unit) => {

            map.set(
                unit.MNF_UNIT,
                unit
            );

        });

        return map;

    }, [apiUnits]);

    // FINANCIAL YEAR HELPERS  

    const visibleFinancialYears = useMemo(() => {

        return getVisibleFinancialYears(
            financialYearLabels
        );

    }, [financialYearLabels, systemDate]);


    const groupedQuarterLabels = useMemo(() => {

        return groupQuarterLabelsByFY(
            quarterlyLabels
        );

    }, [quarterlyLabels, systemDate]);

    /* COMMON COLUMN HELPERS                       */

    const unitColumn = {
        title: "Units",
        dataIndex: "unitName",
        key: "unitName",
        fixed: "left",
        width: 160,

        render: (text, record) => {

            if (record.isOverall) {
                return (
                    <span
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 800,
                            fontSize: 16,
                        }}
                    >
                        <Sigma
                            size={18}
                            color="#155EEF"
                        />

                        {text}
                    </span>
                );
            }

            return (
                <span
                    style={{
                        fontWeight: 500,
                    }}
                >
                    {text}
                </span>
            );

        },
    };


    const cellRender = (value) => {
        return formatCellValue(
            value,
            valueMode
        );
    };

    // The Overall column always shows the API's raw total (which includes
    // the Upto figure) - unless the Upto column is hidden, in which case its
    // contribution is subtracted so the two stay consistent.
    const overallCellRender = (value, record) => {
        if (valueMode === "PERCENT" || showUptoMarch) {
            return cellRender(value);
        }

        const uptoVal = toNumber(record?.upto);

        return cellRender((toNumber(value) - uptoVal).toFixed(2));
    };

    /*                          YEAR WISE COLUMN GENERATION                   */


    const getYearWiseColumns = () => {

        const columns = [
            unitColumn,
        ];


        // Upto Column
        // Hidden in Percent Mode, and hidden unless the toggle is on.

        if (valueMode !== "PERCENT" && showUptoMarch) {

            columns.push({
                title: getYearWiseUptoLabel(),
                dataIndex: "upto",
                key: "upto",
                width: 150,
                align: "right",
                render: cellRender, ...centerHeader,
            });

        }

        // Financial Year Columns.

        visibleFinancialYears.forEach((fy) => {
            const overallVal = overall?.financial_years?.[fy];
            if (overall && toNumber(overallVal) === 0) {
                return;
            }

            columns.push({

                title: fy,
                dataIndex: fy,
                key: fy,
                width: 100,
                align: "right",
                render: cellRender, ...centerHeader,

            });

        });


        // Overall Column.

        columns.push({

            title: valueMode === "PERCENT"
                ? "Overall %"
                : "Overall",

            dataIndex: "overall",
            key: "overall",
            width: 100,
            align: "right",
            fixed: "right",
            render: overallCellRender, ...centerHeader,

        });


        return columns;

    };



    /*                        QUARTER WISE COLUMN GENERATION                  */


    const getQuarterWiseColumns = () => {

        const columns = [
            unitColumn,
        ];


        // Upto Mar Column.
        // Hidden in Percent Mode, and hidden unless the toggle is on.

        if (valueMode !== "PERCENT" && showUptoMarch) {

            columns.push({

                title: getQuarterWiseUptoLabel(),
                dataIndex: "upto",
                key: "upto",
                width: 150,
                align: "right",
                render: cellRender, ...centerHeader,

            });

        }


        // Quarter Columns grouped FY wise.

        visibleFinancialYears.forEach((fy) => {

            const overallVal = overall?.financial_years?.[fy];
            if (overall && toNumber(overallVal) === 0) {
                return;
            }

            const quarterKeys =
                groupedQuarterLabels[fy] || [];


            const children = [];


            // Q1, Q2, Q3, Q4

            quarterKeys.forEach((quarter) => {
                const overallQuarterVal = overall?.quarters?.[quarter];
                if (overall && toNumber(overallQuarterVal) === 0) {
                    return;
                }

                children.push({

                    title: getQuarterLabel(quarter),
                    dataIndex: quarter,
                    key: quarter,
                    width: 90,
                    align: "right",
                    render: cellRender, ...centerHeader,

                });

            });


            // Total Column of that FY.

            children.push({

                title: "Total",
                dataIndex: `${fy}-TOTAL`,
                key: `${fy}-TOTAL`,
                width: 90,
                align: "right",
                render: cellRender, ...centerHeader,

            });


            columns.push({

                title: fy,
                children,

            });

        });


        // Overall Column.

        columns.push({

            title: valueMode === "PERCENT"
                ? "Overall %"
                : "Overall",

            dataIndex: "overall",
            key: "overall",
            width: 100,
            align: "right",
            fixed: "right",
            render: overallCellRender, ...centerHeader,
        });


        return columns;

    };

    /*                             DYNAMIC COLUMNS                            */
    const columns = useMemo(() => {

        if (timeFilter === "YEAR") {
            return getYearWiseColumns();
        }

        return getQuarterWiseColumns();

    }, [

        timeFilter,
        valueMode,
        visibleFinancialYears,
        groupedQuarterLabels,
        overall,
        showUptoMarch,

    ]);

    /*                       YEAR WISE TABLE DATA                             */


    const getYearWiseTableData = () => {

        const rows = [];

        /* --------------------------- OVERALL ROW --------------------------- */

        if (overall) {

            const overallRow = {
                key: "overall",
                unitName: "Overall",
                isOverall: true,
            };

            if (valueMode !== "PERCENT") {
                overallRow.upto = getUptoValue(overall);
            }

            visibleFinancialYears.forEach((fy) => {
                overallRow[fy] = overall?.financial_years?.[fy] ?? "0.00";
            });

            overallRow.overall = overall?.total ?? "0.00";

            rows.push(overallRow);

        }

        /* --------------------------- UNIT ROWS ------------------------------ */

        masterUnits.forEach((unit) => {

            const unitReport = unitCostMap.get(unit.MNF_UNIT);
            if (toNumber(unitReport?.total) === 0) return;
            const row = {
                key: unit.MNF_UNIT,
                unitName: unit.UNIT_DISPLAY_NAME,
            };

            if (valueMode !== "PERCENT") {
                row.upto = getUptoValue(unitReport);
            }

            visibleFinancialYears.forEach((fy) => {
                row[fy] = unitReport?.financial_years?.[fy] ?? "0.00";
            });

            row.overall = unitReport?.total ?? "0.00";

            rows.push(row);

        });

        return rows;

    };

    /*                      QUARTER WISE TABLE DATA                           */


    const getQuarterWiseTableData = () => {

        const rows = [];

        /* --------------------------- OVERALL ROW --------------------------- */

        if (overall) {

            const overallRow = {
                key: "overall",
                unitName: "Overall",
                isOverall: true,
            };

            if (valueMode !== "PERCENT") {
                overallRow.upto = getUptoValue(overall);
            }

            visibleFinancialYears.forEach((fy) => {

                const quarterKeys = groupedQuarterLabels[fy] || [];

                quarterKeys.forEach((quarter) => {
                    overallRow[quarter] = overall?.quarters?.[quarter] ?? "0.00";
                });

                overallRow[`${fy}-TOTAL`] = overall?.financial_years?.[fy] ?? "0.00";

            });

            overallRow.overall = overall?.total ?? "0.00";

            rows.push(overallRow);

        }

        /* --------------------------- UNIT ROWS ------------------------------ */

        masterUnits.forEach((unit) => {

            const unitReport = unitCostMap.get(unit.MNF_UNIT);

            const row = {
                key: unit.MNF_UNIT,
                unitName: unit.UNIT_DISPLAY_NAME,
            };

            if (valueMode !== "PERCENT") {
                row.upto = getUptoValue(unitReport);
            }

            visibleFinancialYears.forEach((fy) => {

                const quarterKeys = groupedQuarterLabels[fy] || [];

                quarterKeys.forEach((quarter) => {
                    row[quarter] = unitReport?.quarters?.[quarter] ?? "0.00";
                });

                row[`${fy}-TOTAL`] = unitReport?.financial_years?.[fy] ?? "0.00";

            });

            row.overall = unitReport?.total ?? "0.00";

            rows.push(row);

        });

        return rows;

    };



    /*                            DYNAMIC TABLE DATA                          */


    const tableData = useMemo(() => {

        if (timeFilter === "YEAR") {
            return getYearWiseTableData();
        }

        return getQuarterWiseTableData();

    }, [

        timeFilter,
        valueMode,
        masterUnits,
        overall,
        unitCostMap,
        visibleFinancialYears,
        groupedQuarterLabels,

    ]);



    /*                                  RENDER                                */


    return (
        <div className="pb-14">
            <div className="px-5 flex flex-col gap-6 pt-8">

                {/* HEADER */}

                <div className="flex gap-3">
                    <img
                        src={PMGProjectIcon}
                        alt="icon"
                    />

                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            DM Analytics: PROJECT WISE - UNIT WISE
                        </div>

                        <div className="text-gray-500 text-sm">
                            * All figures are in Rs Crores or in Percent depending on selected filter.
                        </div>
                    </div>
                </div>


                {/* FILTERS */}

                <div className="flex flex-col gap-6 mt-2">

                    <div
                        className="
                        flex
                        flex-wrap
                        items-start
                        justify-start
                        gap-4
                        p-4
                        bg-[#F9FAFB]
                        border
                        border-[#EAECF0]
                        rounded-xl
                    "
                    >

                        {/* Type of Project */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Type of Project
                            </span>

                            <CustomSelectWithFilter
                                data={PROJECT_TYPE_OPTIONS}
                                value={projectType}
                                onChange={setProjectType}
                                placeholder="Type of Project"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Cost Level */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Cost Level
                            </span>

                            <CustomSelectWithFilter
                                data={COST_LEVEL_OPTIONS}
                                value={costLevel}
                                onChange={setCostLevel}
                                placeholder="Cost Level"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Value Mode */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Value Mode
                            </span>

                            <CustomSelectWithFilter
                                data={VALUE_MODE_OPTIONS}
                                value={valueMode}
                                onChange={setValueMode}
                                placeholder="Value Mode"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Time Filter */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Time Filter
                            </span>

                            <CustomSelectWithFilter
                                data={TIME_FILTER_OPTIONS}
                                value={timeFilter}
                                onChange={setTimeFilter}
                                placeholder="Time Filter"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Projects Filter */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Projects Filter
                            </span>

                            <CustomMultiSelect
                                data={visibleProjectsForFilter.map((project) => ({
                                    label: project.PROJ_NAME,
                                    value: makeProjectKey(
                                        project.IO_NO,
                                        project.REV_NO
                                    ),
                                }))}
                                value={selectedProjectKeys}
                                onChange={setSelectedProjectKeys}
                                placeholder="All Projects"
                                className="w-[300px]"
                            />
                        </div>


                        {/* Show Upto March — only meaningful in Crore mode */}

                        {valueMode !== "PERCENT" && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Inc. Upto March '{String(CURRENT_FY_START).slice(-2)}
                                </span>

                                <div className="flex items-center h-[42px] px-3 rounded-md border border-[#3b82f6] bg-[#eff6ff]">
                                    <Switch
                                        checked={showUptoMarch}
                                        onChange={setShowUptoMarch}
                                    />
                                </div>
                            </div>
                        )}

                    </div>

                </div>


                {/* TABLE */}

                {gettingUnitWiseCost ? (

                    <div className="flex items-center justify-center py-10">
                        <Loader2
                            className="h-8 w-8 animate-spin text-[#155EEF]"
                        />
                    </div>

                ) : (

                    <div>
                        <CustomTable
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            scroll={{
                                x: "max-content",
                                y: 600,
                            }}
                            rowKey="key"
                            rowClassName={(record) => {

                                if (record.isOverall) {
                                    return "dm-grand-total-row";
                                }

                                return "";

                            }}
                        />
                    </div>

                )}

            </div>
        </div>
    );

};

export default DMUnitWiseReport;
